import { useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { Box, Select, Text, Tooltip } from "@mantine/core";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontSize } from "@tiptap/extension-text-style/font-size";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import TableCell from "@tiptap/extension-table-cell";
import CodeBlock from "@tiptap/extension-code-block";
import Placeholder from "@tiptap/extension-placeholder";
import { IconTable, IconMaximize, IconMinimize, IconMoodSmile } from "@tabler/icons-react";
import EmojiPicker from "emoji-picker-react";
import TablePicker from "./TablePicker";
import TableMenu from "./TableMenu";
import { FontFamily } from "./extensions/FontFamily";

/**
 * Rich text editor using @mantine/tiptap.
 * Same feature set as the reference RichTextEditor (bold, italic, underline, highlight,
 * headings, font family, color, lists, alignment, table, link, image, code block,
 * fullscreen, code view, help).
 *
 * Required packages:
 *   npm install @mantine/tiptap @mantine/core @mantine/hooks @tabler/icons-react
 *   npm install @tiptap/react @tiptap/pm @tiptap/starter-kit @tiptap/extension-underline
 *   npm install @tiptap/extension-text-style @tiptap/extension-color @tiptap/extension-highlight
 *   npm install @tiptap/extension-link @tiptap/extension-image @tiptap/extension-text-align
 *   npm install @tiptap/extension-table @tiptap/extension-table-row @tiptap/extension-table-header
 *   npm install @tiptap/extension-table-cell @tiptap/extension-code-block @tiptap/extension-placeholder
 *
 * In your app root, import styles:
 *   import '@mantine/core/styles.css';
 *   import '@mantine/tiptap/styles.css';
 */
function MantineRichTextEditor({ value, onChange, placeholder, minHeight = 300, maxHeight = 600, maxLength = null, isMinimumOptions = false }) {
  const { t } = useTranslation();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showCodeView, setShowCodeView] = useState(false);
  const [selectedFont, setSelectedFont] = useState("system-ui");
  const [selectedFontSize, setSelectedFontSize] = useState("16");
  const [textColor, setTextColor] = useState("#000000");
  const [highlightColor, setHighlightColor] = useState("#fef08a");
  const [showTablePicker, setShowTablePicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [tableMenuPosition, setTableMenuPosition] = useState(null);
  const editorRef = useRef(null);
  const containerRef = useRef(null);

  const editor = useEditor({
    shouldRerenderOnTransaction: true,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        codeBlock: false,
        link: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-blue-500 underline" },
      }),
      Underline,
      TextStyle,
      FontSize,
      FontFamily,
      Color,
      Highlight.configure({ multicolor: true }),
      Image.configure({ inline: true, allowBase64: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: "border-collapse",
          style: "border: 1px solid #d1d5db",
        },
      }),
      TableRow,
      TableHeader.configure({
        HTMLAttributes: {
          style: "border: 1px solid #d1d5db; padding: 6px 8px;",
        },
      }),
      TableCell.configure({
        HTMLAttributes: {
          style: "border: 1px solid #d1d5db; padding: 6px 8px;",
        },
      }),
      CodeBlock.configure({
        HTMLAttributes: {
          style:
            "white-space: pre-wrap; word-break: break-word; overflow-wrap: break-word; max-width: 100%;",
        },
      }),
      Placeholder.configure({ placeholder: placeholder || t('editor.placeholder') }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[200px] p-4",
      },
    },
  });

  useEffect(() => {
    if (!editor || value === undefined) return;
    // While focused, parent `value` may differ from getHTML() (e.g. html-entities decode
    // in onChange). Syncing would call setContent every keystroke and break lists/cursor.
    if (editor.isFocused) return;
    const currentContent = editor.getHTML();
    if (value !== currentContent) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;

    const updateTableMenuPosition = () => {
      if (!editor.can().deleteTable()) {
        setTableMenuPosition(null);
        return;
      }
      try {
        const { state } = editor;
        const { selection } = state;
        const { $anchor } = selection;
        let depth = $anchor.depth;
        let tableCell = null;
        while (depth > 0) {
          const node = $anchor.node(depth);
          if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
            tableCell = node;
            break;
          }
          depth--;
        }
        if (tableCell) {
          const pos = $anchor.before(depth);
          const dom = editor.view.nodeDOM(pos);
          if (dom && dom instanceof HTMLElement) {
            const cellElement = dom.closest("td, th") || dom;
            if (cellElement instanceof HTMLElement) {
              const rect = cellElement.getBoundingClientRect();
              const editorRect = editorRef.current?.getBoundingClientRect();
              if (editorRect) {
                const scrollTop = editorRef.current?.scrollTop || 0;
                setTableMenuPosition({
                  top: rect.bottom - editorRect.top + scrollTop + 8,
                  left: rect.left - editorRect.left,
                });
              }
            }
          }
        } else {
          setTableMenuPosition(null);
        }
      } catch {
        setTableMenuPosition(null);
      }
    };

    editor.on("selectionUpdate", updateTableMenuPosition);
    editor.on("focus", updateTableMenuPosition);
    return () => {
      editor.off("selectionUpdate", updateTableMenuPosition);
      editor.off("focus", updateTableMenuPosition);
    };
  }, [editor]);

  if (!editor) return null;

  const plainTextLength = editor.getText().length;
  const resolvedMaxLength = maxLength != null && maxLength !== "" ? Number(maxLength) : NaN;
  const showCharLimit = Number.isFinite(resolvedMaxLength) && resolvedMaxLength > 0;

  const handleInsertTable = (rows, cols) => {
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: false }).run();
    setShowTablePicker(false);
  };

  const toggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleColorChange = (color) => {
    setTextColor(color);
    editor.chain().focus().setColor(color).run();
  };

  const handleHighlightColorChange = (color) => {
    setHighlightColor(color);
    editor.chain().focus().toggleHighlight({ color }).run();
  };

  const handleFontChange = (font) => {
    setSelectedFont(font);
    editor.chain().focus().setFontFamily(font).run();
  };

  const handleFontSizeChange = (size) => {
    setSelectedFontSize(size);
    if (size) {
      editor.chain().focus().setFontSize(`${size}px`).run();
    } else {
      editor.chain().focus().unsetFontSize().run();
    }
  };

  const getBlockValue = () => {
    if (editor.isActive("heading", { level: 1 })) return "heading1";
    if (editor.isActive("heading", { level: 2 })) return "heading2";
    if (editor.isActive("heading", { level: 3 })) return "heading3";
    if (editor.isActive("heading", { level: 4 })) return "heading4";
    if (editor.isActive("heading", { level: 5 })) return "heading5";
    if (editor.isActive("heading", { level: 6 })) return "heading6";
    if (editor.isActive("blockquote")) return "blockquote";
    if (editor.isActive("code")) return "code";
    return "paragraph";
  };

  const handleBlockChange = (val) => {
    if (val === "paragraph") {
      editor.chain().focus().setParagraph().run();
    } else if (val?.startsWith("heading")) {
      const level = parseInt(val.replace("heading", ""), 10);
      editor.chain().focus().toggleHeading({ level }).run();
    } else if (val === "blockquote") {
      editor.chain().focus().toggleBlockquote().run();
    } else if (val === "code") {
      editor.chain().focus().setCode().run();
    }
  };

  const fontSizeOptions = [
    { value: "8", label: "8" },
    { value: "9", label: "9" },
    { value: "10", label: "10" },
    { value: "11", label: "11" },
    { value: "12", label: "12" },
    { value: "14", label: "14" },
    { value: "16", label: "16" },
    { value: "18", label: "18" },
    { value: "24", label: "24" },
    { value: "30", label: "30" },
    { value: "36", label: "36" },
    { value: "48", label: "48" },
    { value: "60", label: "60" },
    { value: "72", label: "72" },
    { value: "96", label: "96" },
  ];

  const fontOptions = [
    { value: "system-ui", label: t('editor.fonts.systemUI') },
    { value: "Arial", label: "Arial" },
    { value: "Helvetica", label: "Helvetica" },
    { value: "Times New Roman", label: "Times New Roman" },
    { value: "Courier New", label: "Courier New" },
    { value: "Georgia", label: "Georgia" },
    { value: "Verdana", label: "Verdana" },
  ];

  const blockOptions = [
    { value: "paragraph", label: t('editor.blocks.normal') },
    { value: "blockquote", label: t('editor.blocks.blockquote') },
    { value: "code", label: t('editor.blocks.code') },
    { value: "heading1", label: t('editor.blocks.header1') },
    { value: "heading2", label: t('editor.blocks.header2') },
    { value: "heading3", label: t('editor.blocks.header3') },
    { value: "heading4", label: t('editor.blocks.header4') },
    { value: "heading5", label: t('editor.blocks.header5') },
    { value: "heading6", label: t('editor.blocks.header6') },
  ];

  return (
    <Box
      ref={containerRef}
      className="mantine-rte-container"
      style={{
        borderRadius: 8,
        overflow: "hidden",
        ...(isFullscreen && {
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          backgroundColor: "white",
        }),
      }}
    >
      <RichTextEditor editor={editor} onSourceCodeTextSwitch={setShowCodeView}>
        <RichTextEditor.Toolbar>
          {/* Block / heading selector */}
          {!isMinimumOptions && (
            <Select
              value={getBlockValue()}
              onChange={handleBlockChange}
              data={blockOptions}
              size="xs"
              w={140}
              styles={{
                input: {
                  border: "none",
                  minHeight: 32,
                  fontWeight: 500,
                },
              }}
            />
          )}

          {/* Font size */}
          {!isMinimumOptions && (
            <Select
              value={selectedFontSize}
              onChange={handleFontSizeChange}
              data={fontSizeOptions}
              size="xs"
              w={70}
              styles={{
                input: {
                  border: "none",
                  minHeight: 32,
                },
              }}
            />            
          )}

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
            <RichTextEditor.Strikethrough />
            <RichTextEditor.ClearFormatting />
            <Tooltip label={t('editor.tooltips.highlightColor')}>
              <RichTextEditor.Control aria-label={t('editor.tooltips.highlightColor')}>
                <input
                  type="color"
                  value={highlightColor}
                  onChange={(e) => handleHighlightColorChange(e.target.value)}
                  style={{
                    width: 32,
                    height: 32,
                    border: "none",
                    borderRadius: 2,
                    cursor: "pointer",
                    padding: 0,
                    backgroundColor: "transparent",
                  }}
                />
              </RichTextEditor.Control>
            </Tooltip>
            <RichTextEditor.Highlight />
            {/* <RichTextEditor.Code /> */}
          </RichTextEditor.ControlsGroup>

          {/* Font family */}
          {!isMinimumOptions && (
            <Select
              value={selectedFont}
              onChange={handleFontChange}
              data={fontOptions}
              size="xs"
              w={140}
              styles={{
                input: {
                  border: "none",
                  minHeight: 32,
                },
              }}
            />
          )}

          {/* Text color */}
          <Tooltip label={t('editor.tooltips.textColor')}>
            <RichTextEditor.Control aria-label={t('editor.tooltips.textColor')}>
              <input
                type="color"
                value={textColor}
                onChange={(e) => handleColorChange(e.target.value)}
                style={{
                  width: 32,
                  height: 32,
                  border: "none",
                  borderRadius: 2,
                  cursor: "pointer",
                  padding: 0,
                  backgroundColor: "transparent",
                }}
              />
            </RichTextEditor.Control>
          </Tooltip>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.AlignLeft />
            <RichTextEditor.AlignCenter />
            <RichTextEditor.AlignRight />
            <RichTextEditor.AlignJustify />
          </RichTextEditor.ControlsGroup>

          {/* Table */}
          {!isMinimumOptions && (
            <Box style={{ position: "relative" }}>
              <Tooltip label={t('editor.tooltips.insertTable')}>
                <RichTextEditor.Control
                  onClick={() => setShowTablePicker((p) => !p)}
                  aria-label={t('editor.tooltips.insertTable')}
                  active={showTablePicker}
                >
                  <IconTable size={16} stroke={1.5} />
                </RichTextEditor.Control>
              </Tooltip>
              {showTablePicker && (
                <>
                  {/* Click-away overlay: closes picker when clicking outside */}
                  <Box
                    onClick={() => setShowTablePicker(false)}
                    style={{ position: "fixed", inset: 0, zIndex: 1299 }}
                    aria-hidden
                  />
                  {/* Picker positioned below the toolbar button (sibling of overlay, so position is relative to this Box) */}
                  <TablePicker
                    onSelect={handleInsertTable}
                    onClose={() => setShowTablePicker(false)}
                  />
                </>
              )}
            </Box>
          )}

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Link />
            <RichTextEditor.Unlink />
          </RichTextEditor.ControlsGroup>

          {/* Emoji picker */}
          <Box style={{ position: "relative" }}>
            <Tooltip label={t('editor.tooltips.insertEmoji')}>
              <RichTextEditor.Control
                onClick={() => setShowEmojiPicker((p) => !p)}
                aria-label={t('editor.tooltips.insertEmoji')}
                active={showEmojiPicker}
              >
                <IconMoodSmile size={16} stroke={1.5} />
              </RichTextEditor.Control>
            </Tooltip>
            {showEmojiPicker && (
              <>
                <Box
                  onClick={() => setShowEmojiPicker(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 1299 }}
                  aria-hidden
                />
                <Box
                  style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    zIndex: 1300,
                  }}
                >
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      editor?.chain().focus().insertContent(emojiData.emoji).run();
                      setShowEmojiPicker(false);
                    }}
                    width={320}
                    height={400}
                  />
                </Box>
              </>
            )}
          </Box>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.CodeBlock />
          </RichTextEditor.ControlsGroup>

          <Tooltip label={isFullscreen ? t('editor.tooltips.exitFullscreen') : t('editor.tooltips.fullscreen')}>
            <RichTextEditor.Control
              onClick={toggleFullscreen}
              aria-label={t('editor.tooltips.fullscreen')}
            >
              {isFullscreen ? (
                <IconMinimize size={16} stroke={1.5} />
              ) : (
                <IconMaximize size={16} stroke={1.5} />
              )}
            </RichTextEditor.Control>
          </Tooltip>
        </RichTextEditor.Toolbar>

        <Box
          ref={editorRef}
          style={{
            position: "relative",
            minHeight: minHeight,
            maxHeight: isFullscreen ? "calc(100vh - 64px)" : maxHeight,
            overflowY: "auto",
            backgroundColor: "white",
          }}
        >
          <RichTextEditor.Content />
          {tableMenuPosition && editor.can().deleteTable() && (
            <Box
              style={{
                position: "absolute",
                top: tableMenuPosition.top,
                left: tableMenuPosition.left,
                zIndex: 1400,
              }}
            >
              <TableMenu editor={editor} />
            </Box>
          )}
        </Box>
      </RichTextEditor>
      {showCharLimit && (
        <Text
          size="sm"
          fw={600}
          ta="right"
          style={{
            margin: 0,
            padding: "4px 8px 8px",
            color:
              plainTextLength <= resolvedMaxLength
                ? "#AFBED3"
                : "var(--danger-color)",
          }}
        >
          {plainTextLength} / {resolvedMaxLength}
        </Text>
      )}
    </Box>
  );
}

export default MantineRichTextEditor;
